import type { QuantityKind, UnitFor } from './registry';

/**
 * Which unit codes `Intl` can render, and what the rest are called.
 *
 * `Intl.NumberFormat` with `style: 'unit'` produces a localized label for free, correct in every
 * locale and maintained by CLDR rather than by us. It accepts only the sanctioned identifier list
 * in ECMA-402, plus compounds built from two sanctioned members, and throws `RangeError` on
 * anything else. Fourteen of this package's twenty-two codes are covered; eight are not, and no
 * amount of composition rescues them because their components are themselves unsanctioned.
 *
 * So labels are hybrid, and this module is the seam. Nothing else in the package needs to know
 * which side of it a unit falls on.
 */

/**
 * Our code to the `Intl` identifier, or `null` where none exists.
 *
 * Total per kind, so a unit added to the registry fails to compile here until somebody decides
 * which side of the gap it sits on. That is the whole point of writing it out rather than deriving
 * it: the classification is a fact about ICU that has to be checked, not inferred.
 */
export const INTL_UNIT_BY_CODE: {
  readonly [K in QuantityKind]: Readonly<Record<UnitFor<K>, string | null>>;
} = {
  distance: { KM: 'kilometer', MILES: 'mile' },
  speed: {
    KMH: 'kilometer-per-hour',
    MPH: 'mile-per-hour',
    MPS: 'meter-per-second',
  },
  // `gallon` is the US gallon; CLDR names the imperial one `gallon-imperial`, which ECMA-402 does
  // not sanction.
  capacity: { LITER: 'liter', GAL_US: 'gallon', GAL_UK: null },
  // "100 kilometer" is not a unit, so no per-100-km measure can be expressed at all.
  fuelConsumption: {
    L_PER_100KM: null,
    MPG_US: 'mile-per-gallon',
    MPG_UK: null,
  },
  electricConsumption: { KWH_PER_100KM: null, MI_PER_KWH: null },
  // Neither `kilowatt-hour` nor `watt-hour` is sanctioned, so the unit this product bills in is
  // the one CLDR cannot name.
  energy: { KWH: null },
  // No pressure unit is sanctioned: not `pascal`, not `psi`, not `bar`.
  pressure: { KPA: null, PSI: null },
  temperature: { C: 'celsius', F: 'fahrenheit' },
  emission: {
    GR_PER_KM: 'gram-per-kilometer',
    GR_PER_MI: 'gram-per-mile',
    LB_PER_MI: 'pound-per-mile',
  },
  mass: { KG: 'kilogram', LB: 'pound' },
};

/**
 * The codes `Intl` refuses, listed once.
 *
 * Duplicating what the table above already says, deliberately: it gives the dictionary below a
 * total key type, so a new gap unit cannot be added without a label in every seeded language.
 * `catalog.test.ts` holds the two in agreement, and holds both against what ICU actually does.
 */
export const GAP_UNIT_CODES = [
  'GAL_UK',
  'KPA',
  'KWH',
  'KWH_PER_100KM',
  'L_PER_100KM',
  'MI_PER_KWH',
  'MPG_UK',
  'PSI',
] as const;

export type GapUnitCode = (typeof GAP_UNIT_CODES)[number];

type GapLabels = Readonly<Record<GapUnitCode, string>>;

/**
 * The labels the core owns, by language subtag.
 *
 * Keyed on the language rather than the full locale because these eight are symbols, not words:
 * `kWh` is `kWh` in every Latin-script market this product serves, and a per-locale table would be
 * eight identical rows pretending to be a translation. Where a language genuinely disagrees with
 * itself by region, {@link GAP_LABEL_OVERRIDES} carries the exception.
 *
 * This dictionary is package data and not an `i18next` namespace, which is decision R2:
 * `i18next` is not federation-shared, so each app holds its own instance and a label resolved
 * through one would render as a raw key inside a remote.
 */
export const GAP_LABEL_CATALOG: Readonly<Record<string, GapLabels>> = {
  en: {
    GAL_UK: 'imp gal',
    KPA: 'kPa',
    KWH: 'kWh',
    KWH_PER_100KM: 'kWh/100 km',
    L_PER_100KM: 'L/100 km',
    MI_PER_KWH: 'mi/kWh',
    MPG_UK: 'imp mpg',
    PSI: 'psi',
  },
  fr: {
    GAL_UK: 'gal imp',
    KPA: 'kPa',
    KWH: 'kWh',
    KWH_PER_100KM: 'kWh/100 km',
    L_PER_100KM: 'L/100 km',
    MI_PER_KWH: 'mi/kWh',
    MPG_UK: 'mi/gal imp',
    PSI: 'psi',
  },
  es: {
    GAL_UK: 'gal imp',
    KPA: 'kPa',
    KWH: 'kWh',
    KWH_PER_100KM: 'kWh/100 km',
    L_PER_100KM: 'L/100 km',
    MI_PER_KWH: 'mi/kWh',
    MPG_UK: 'mi/gal imp',
    PSI: 'psi',
  },
};

/**
 * Where a region disagrees with the rest of its language.
 *
 * Canadian French writes tyre pressure as `lb/po²` — livres par pouce carré — which is what
 * `fr-CA/common.json` shows today and what a Québécois driver expects to read. France quotes
 * pressure in bar and, on the rare occasion it names psi, calls it `psi`. One table entry is
 * cheaper than splitting the language rows.
 */
export const GAP_LABEL_OVERRIDES: Readonly<
  Partial<Record<string, Readonly<Partial<GapLabels>>>>
> = {
  'fr-CA': { PSI: 'lb/po²' },
};

/**
 * The unit ICU is asked about when the package needs to know a locale's own conventions.
 *
 * Sanctioned in every locale, and plain enough to carry no special casing of its own — unlike the
 * temperature units, which CLDR closes up against the number in some locales and not others.
 */
export const REFERENCE_INTL_UNIT = 'kilometer';

/**
 * The gap between a number and its label, when ICU offers no opinion.
 *
 * Most locales separate the two with a plain space, but not all: fr-FR uses a narrow no-break
 * space and fr-CA a no-break space. That is why the separator is read per locale rather than fixed here — a column mixing
 * sides of the CLDR gap would otherwise show two different spacings.
 */
export const FALLBACK_LABEL_SEPARATOR = ' ';
